import {
  AnalysisMetricOutcome,
  ExperimentAnalysisCampaignStats,
  ExperimentAnalysisExclusion,
  ExperimentAnalysisMetricResult,
  ExperimentAnalysisResultArtifact,
  ExperimentAnalysisTestMetricDetails,
  ExperimentAnalysisTestStats,
} from "@/http/api-types";

export const ANALYSIS_STAGES = ["primary", "secondary", "revenue"] as const;
export type AnalysisStage = (typeof ANALYSIS_STAGES)[number];

// One metric is surfaced per stage, the per-EUR one: XPC for primary/secondary (presented as IPC/APC) and ROAS
// for revenue. (The artifact still carries the per-1000-impressions XPM/RPM — they're just not displayed.)
export const ANALYSIS_STAGE_METRIC = { primary: "XPC", secondary: "XPC", revenue: "ROAS" } as const;
export type AnalysisMetric = (typeof ANALYSIS_STAGE_METRIC)[AnalysisStage];

export type CampaignOutcome = "positive" | "negative" | "neutral" | "inconclusive";

export type AnalysisOutcomeCell = {
  stage: AnalysisStage;
  metric: AnalysisMetric;
  // False when the result predates the stage (revenue before it was added); its counts are then all zero.
  analysed: boolean;
  won: number;
  lost: number;
  neutral: number;
  inconclusive: number;
  total: number;
};

export type AnalysisForestRow = {
  campaignId: string;
  // The control campaign this test is compared against (custom view; the row is one control's pair).
  controlId?: string;
  control: ExperimentAnalysisCampaignStats;
  test: ExperimentAnalysisTestStats;
  metricDetails: ExperimentAnalysisTestMetricDetails | null;
  liftMeanPct: number | null;
  liftMedianPct: number | null;
  liftCILowerPct: number | null;
  liftCIUpperPct: number | null;
  pTestBetter: number | null;
  outcome: CampaignOutcome;
};

// Low-volume posteriors can explode to unusable lift percentages; keep the chart axis readable.
const FOREST_DOMAIN_LIMIT_PCT = 1000;
const FOREST_DOMAIN_PADDING_RATIO = 0.05;
const FOREST_WIN_PROBABILITY_THRESHOLD = 0.95;

export function isExperimentAnalysisResultArtifact(value: unknown): value is ExperimentAnalysisResultArtifact {
  if (!value || typeof value !== "object") {
    return false;
  }

  return "campaigns" in value && "summary" in value && "variants" in value;
}

export function getSummaryStatus(artifact: ExperimentAnalysisResultArtifact): string | undefined {
  return typeof artifact.summary.status === "string" ? artifact.summary.status : undefined;
}

export function getExclusions(artifact: ExperimentAnalysisResultArtifact): ExperimentAnalysisExclusion[] {
  return artifact.exclusions ?? [];
}

// The stages the result carries. Revenue was added to the analysis later, so results from before don't have it.
export function getAnalysisStages(artifact: ExperimentAnalysisResultArtifact): AnalysisStage[] {
  return ANALYSIS_STAGES.filter(stage => stage !== "revenue" || hasRevenueAnalysis(artifact));
}

function hasRevenueAnalysis(artifact: ExperimentAnalysisResultArtifact): boolean {
  return (
    Object.values(artifact.summary.results ?? {}).some(result => result.revenue != null) ||
    Object.values(artifact.campaigns).some(campaign =>
      Object.values(campaign.tests ?? {}).some(test => test.revenue != null),
    )
  );
}

// Global tabs: the test flights. Derived from summary.results keys ∪ variants values ∪ campaign test keys.
export function getAvailableTestIDs(artifact: ExperimentAnalysisResultArtifact): string[] {
  const ids = new Set<string>();

  for (const testID of Object.values(artifact.variants).flat()) {
    if (testID !== 0) {
      ids.add(String(testID));
    }
  }

  for (const key of Object.keys(artifact.summary.results ?? {})) {
    ids.add(key);
  }

  for (const campaign of Object.values(artifact.campaigns)) {
    for (const key of Object.keys(campaign.tests ?? {})) {
      ids.add(key);
    }
  }

  return Array.from(ids).sort((left, right) => Number(left) - Number(right));
}

// Custom tabs: the positional test-group columns (0-based). N = length of any variants array.
export function buildTestGroupColumns(artifact: ExperimentAnalysisResultArtifact): number[] {
  const length = Object.values(artifact.variants).reduce((max, tests) => Math.max(max, tests.length), 0);
  return Array.from({ length }, (_, index) => index);
}

// Test campaign ids in a positional column (drops 0 = "no campaign for that control").
export function testIDsForColumn(artifact: ExperimentAnalysisResultArtifact, column: number): string[] {
  const ids = new Set<string>();
  for (const tests of Object.values(artifact.variants)) {
    const id = tests[column];
    if (id && id !== 0) {
      ids.add(String(id));
    }
  }
  return Array.from(ids);
}

function makeForestRow(
  campaignId: string,
  control: ExperimentAnalysisCampaignStats,
  test: ExperimentAnalysisTestStats,
  stage: AnalysisStage,
  metric: AnalysisMetric,
  controlId?: string,
): AnalysisForestRow {
  const stageMetrics: Partial<Record<AnalysisMetric, ExperimentAnalysisTestMetricDetails>> | undefined = test[stage];
  const metricDetails = stageMetrics?.[metric] ?? null;

  return {
    campaignId,
    controlId,
    control,
    test,
    metricDetails,
    liftMeanPct: metricDetails?.lift_mean_pct ?? null,
    liftMedianPct: metricDetails?.lift_median_pct ?? null,
    liftCILowerPct: metricDetails?.lift_ci_lower_pct ?? null,
    liftCIUpperPct: metricDetails?.lift_ci_upper_pct ?? null,
    pTestBetter: metricDetails?.p_test_better ?? null,
    outcome: outcomeFromMetricDetails(metricDetails),
  };
}

function sortByLiftDesc(rows: AnalysisForestRow[]): AnalysisForestRow[] {
  return rows.sort((left, right) => compareNullableNumbers(right.liftMeanPct, left.liftMeanPct));
}

// Global: a test flight compared across each campaign it runs in.
export function buildForestRows(
  artifact: ExperimentAnalysisResultArtifact,
  testID: string,
  stage: AnalysisStage,
  metric: AnalysisMetric,
): AnalysisForestRow[] {
  const rows: AnalysisForestRow[] = [];
  for (const [campaignId, campaign] of Object.entries(artifact.campaigns)) {
    const test = campaign.tests?.[testID];
    if (!test) {
      continue;
    }
    rows.push(makeForestRow(campaignId, campaign.control, test, stage, metric));
  }
  return sortByLiftDesc(rows);
}

// Custom: one row per control for the test campaign sitting in this positional column (test group).
export function buildForestRowsForColumn(
  artifact: ExperimentAnalysisResultArtifact,
  column: number,
  stage: AnalysisStage,
  metric: AnalysisMetric,
): AnalysisForestRow[] {
  const rows: AnalysisForestRow[] = [];
  for (const [controlId, tests] of Object.entries(artifact.variants)) {
    const testId = tests[column];
    if (!testId || testId === 0) {
      continue;
    }
    const campaign = artifact.campaigns[controlId];
    const test = campaign?.tests?.[String(testId)];
    if (!campaign || !test) {
      continue;
    }
    rows.push(makeForestRow(String(testId), campaign.control, test, stage, metric, controlId));
  }
  return sortByLiftDesc(rows);
}

export function getForestDomain(rows: AnalysisForestRow[]): [number, number] {
  let lo = 0;
  let hi = 0;

  for (const row of rows) {
    if (row.liftCILowerPct != null) {
      lo = Math.min(lo, capForestDomainValue(row.liftCILowerPct));
    }
    if (row.liftCIUpperPct != null) {
      hi = Math.max(hi, capForestDomainValue(row.liftCIUpperPct));
    }
    if (row.liftMeanPct != null) {
      lo = Math.min(lo, capForestDomainValue(row.liftMeanPct));
      hi = Math.max(hi, capForestDomainValue(row.liftMeanPct));
    }
  }

  if (lo === 0 && hi === 0) {
    return [-50, 50];
  }

  const maxAbsValue = Math.max(Math.abs(lo), Math.abs(hi));
  const padding = Math.max(maxAbsValue * FOREST_DOMAIN_PADDING_RATIO, 1);
  const bound = capForestDomainValue(maxAbsValue + padding);

  return [-bound, bound];
}

export function getAnalysisMemberLabel(): "campaign" {
  return "campaign";
}

// One side of the conversions table. Supply cost rides along because the raw counts are only
// comparable at equal spend — IPC/APC/ROAS are these counts normalised by it.
export type AnalysisConversionSide = {
  supplyCostEUR: number;
  apConversions: number;
  secondaryAPConversions: number;
  apCustomerRevenueEUR: number;
  // Unique users behind the 2nd AP conversions and the revenue; absent for results from before they were reported.
  secondaryConverters?: number;
  revenueConverters?: number;
};

export type AnalysisConversionTotals = {
  control: AnalysisConversionSide;
  test: AnalysisConversionSide;
};

// Stage-independent: the counts hang off the campaign, not off the stage's metric block, so the same totals
// hold for every lens. Only comparisons with data on both sides count: a test flight that didn't run in a
// campaign (or a control without data there) would otherwise add its spend and conversions to one side only.
export function buildConversionTotals(forestRows: AnalysisForestRow[]): AnalysisConversionTotals {
  const totals: AnalysisConversionTotals = { control: emptyConversionSide(), test: emptyConversionSide() };

  for (const row of forestRows) {
    if (row.control.impressions == null || row.test.impressions == null) {
      continue;
    }
    addConversionStats(totals.control, row.control);
    addConversionStats(totals.test, row.test);
  }

  return totals;
}

// Percent change from control to test. Null when control is 0 — there is no baseline to divide by.
export function percentChange(control: number, test: number): number | null {
  if (control === 0) {
    return null;
  }
  return ((test - control) / control) * 100;
}

function emptyConversionSide(): AnalysisConversionSide {
  return { supplyCostEUR: 0, apConversions: 0, secondaryAPConversions: 0, apCustomerRevenueEUR: 0 };
}

function addConversionStats(side: AnalysisConversionSide, stats: ExperimentAnalysisCampaignStats): void {
  side.supplyCostEUR += stats.supply_cost_eur ?? 0;
  side.apConversions += stats.ap_conversions ?? 0;
  side.secondaryAPConversions += stats.secondary_ap_conversions ?? 0;
  side.apCustomerRevenueEUR += stats.ap_customer_revenue_eur ?? 0;
  if (stats.secondary_converters != null) {
    side.secondaryConverters = (side.secondaryConverters ?? 0) + stats.secondary_converters;
  }
  if (stats.revenue_converters != null) {
    side.revenueConverters = (side.revenueConverters ?? 0) + stats.revenue_converters;
  }
}

// Outcome counts per stage (and its metric) for one test group, one cell for every stage so the layout is the
// same for every result. Reads the authoritative `summary.results[key]` (4-state counts incl. inconclusive);
// falls back to counting the forest rows' outcomes only when results are absent. A stage the result doesn't
// carry gets zero counts — counting its missing blocks would report every comparison as neutral.
export function buildOutcomeMatrix(
  artifact: ExperimentAnalysisResultArtifact,
  summaryKey: string,
  buildRows: (stage: AnalysisStage, metric: AnalysisMetric) => AnalysisForestRow[],
): AnalysisOutcomeCell[] {
  const summary = artifact.summary.results?.[summaryKey];
  const analysedStages = getAnalysisStages(artifact);

  return ANALYSIS_STAGES.map(stage => {
    const metric = ANALYSIS_STAGE_METRIC[stage];
    if (!analysedStages.includes(stage)) {
      return { stage, metric, analysed: false, won: 0, lost: 0, neutral: 0, inconclusive: 0, total: 0 };
    }

    const stageSummary: Partial<Record<AnalysisMetric, ExperimentAnalysisMetricResult>> | undefined = summary?.[stage];
    const result = stageSummary?.[metric];
    const counts = result
      ? {
          won: result.won ?? 0,
          lost: result.lost ?? 0,
          neutral: result.neutral ?? 0,
          inconclusive: result.inconclusive ?? 0,
        }
      : countOutcomes(buildRows(stage, metric));

    const total = counts.won + counts.lost + counts.neutral + counts.inconclusive;
    return { stage, metric, analysed: true, ...counts, total };
  });
}

// A lens has data to show only with at least one conclusive comparison in the test group. Without one (every
// comparison too thin to judge or missing data on one side, no comparisons at all, or a result from before the
// stage existed) it shows "no data" — typically ROAS for audiences that hardly pay.
export function hasConclusiveComparison(cell: AnalysisOutcomeCell): boolean {
  return cell.won + cell.lost + cell.neutral > 0;
}

type OutcomeCounts = { won: number; lost: number; neutral: number; inconclusive: number };

function countOutcomes(rows: AnalysisForestRow[]): OutcomeCounts {
  const counts: OutcomeCounts = { won: 0, lost: 0, neutral: 0, inconclusive: 0 };
  for (const row of rows) {
    const outcome = row.metricDetails?.outcome ?? campaignToMetricOutcome(row.outcome);
    counts[outcome] += 1;
  }
  return counts;
}

function campaignToMetricOutcome(outcome: CampaignOutcome): AnalysisMetricOutcome {
  if (outcome === "positive") {
    return "won";
  }
  if (outcome === "negative") {
    return "lost";
  }
  if (outcome === "inconclusive") {
    return "inconclusive";
  }
  return "neutral";
}

// Colour the forest bar from the artifact's authoritative `outcome` (Edvin: do not recalculate — the
// summary counts these). Only derive from the CI when `outcome` is absent (older artifacts).
function outcomeFromMetricDetails(metricDetails: ExperimentAnalysisTestMetricDetails | null): CampaignOutcome {
  if (!metricDetails) {
    return "neutral";
  }

  switch (metricDetails.outcome) {
    case "won":
      return "positive";
    case "lost":
      return "negative";
    case "neutral":
      return "neutral";
    case "inconclusive":
      return "inconclusive";
  }

  if ((metricDetails.p_test_better ?? 0) >= FOREST_WIN_PROBABILITY_THRESHOLD) {
    return "positive";
  }
  if ((metricDetails.lift_ci_lower_pct ?? 0) > 0) {
    return "positive";
  }
  if ((metricDetails.lift_ci_upper_pct ?? 0) < 0) {
    return "negative";
  }
  return "neutral";
}

function compareNullableNumbers(left: number | null, right: number | null): number {
  const normalizedLeft = left ?? Number.NEGATIVE_INFINITY;
  const normalizedRight = right ?? Number.NEGATIVE_INFINITY;

  return normalizedLeft - normalizedRight;
}

function capForestDomainValue(value: number): number {
  return Math.max(-FOREST_DOMAIN_LIMIT_PCT, Math.min(FOREST_DOMAIN_LIMIT_PCT, value));
}
